import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowRight, Loader2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /my-orders — Customer's order history page.
 *
 * Shows every custom order they've placed (any status), with permanent access
 * to the original form submission, every vendor delivery file, and revision
 * history. Anything they bought can be re-downloaded forever.
 */

const STATUS_STYLES = {
  pending_payment:     { label: 'Awaiting Payment',  bg: 'bg-amber-500/15 text-amber-200 border-amber-400/30' },
  active:              { label: 'In Progress',       bg: 'bg-blue-500/15 text-blue-200 border-blue-400/30' },
  delivered:           { label: 'Delivered',         bg: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30' },
  revision_requested:  { label: 'Revision Requested', bg: 'bg-orange-500/15 text-orange-200 border-orange-400/30' },
  completed:           { label: 'Completed',         bg: 'bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30' },
  cancelled:           { label: 'Cancelled',         bg: 'bg-red-500/15 text-red-200 border-red-400/30' },
};

export default function MyOrders() {
  const { getAuthHeaders, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/custom-orders/mine?role=buyer`, { headers: getAuthHeaders() })
      .then((r) => r.ok ? r.json() : { orders: [] })
      .then((d) => setOrders(d.orders || []))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="min-h-screen bg-[#0B0D2B]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 pt-24 pb-16" data-testid="my-orders-page">
        <div className="mb-6">
          <h1 className="font-syne text-3xl sm:text-4xl font-bold text-white">My Orders</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Every custom order you've placed. Re-download files anytime, including completed ones.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-fuchsia-300" /></div>
        ) : orders.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center">
            <Package size={40} className="mx-auto text-zinc-500 mb-3" />
            <p className="text-white font-bold">No orders yet</p>
            <p className="text-zinc-400 text-sm mt-1">
              When a vendor accepts your inquiry, your custom order will appear here.
            </p>
            <Link to="/browse" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-fuchsia-500/20 text-fuchsia-200 text-sm font-bold hover:bg-fuchsia-500/30">
              Browse vendors →
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => {
              const status = STATUS_STYLES[o.status] || STATUS_STYLES.active;
              return (
                <Link
                  key={o.order_id}
                  to={`/orders/${o.order_id}`}
                  className="block rounded-2xl bg-[#13153A]/70 border border-white/10 hover:border-fuchsia-400/40 p-5 transition-all group"
                  data-testid={`my-order-${o.order_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${status.bg}`}>{status.label}</span>
                        <span className="text-zinc-500 text-[10px]">#{o.order_id.slice(-8)}</span>
                      </div>
                      <p className="text-white font-bold group-hover:text-fuchsia-200 transition-colors truncate">{o.title}</p>
                      <p className="text-zinc-400 text-xs mt-1">
                        ${o.price.toFixed(2)} · {o.delivery_days}d · ordered {new Date(o.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <ArrowRight size={18} className="text-zinc-500 group-hover:text-fuchsia-300 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
